'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { useTranslations } from 'next-intl';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { HeroCardRootProps, PreselectedTab } from './types';

interface HeroCardTabsProps extends HeroCardRootProps {
  tabOneLabel: string;
  tabTwoLabel: string;
  /** Fragment that opens tab 1 from an external link. Empty = no anchor. */
  tabOneAnchor: string;
  /** Fragment that opens tab 2 from an external link. Empty = no anchor. */
  tabTwoAnchor: string;
  preselectedTab: PreselectedTab;
  renderTabOne: () => ReactNode;
  renderTabTwo: () => ReactNode;
}

/**
 * The "Lasche" — the card-internal two-tab strip shared by both tabbed cards.
 *
 * Card-internal deliberately: the Hero core only provides the slot and does not
 * implement tab logic (see 10.03.02.01.06-Hero AC 4), and one card carries both
 * tab bodies. Written here rather than extracted from `SubjectsWidgetComponent`
 * because that tablist shares neither markup (a circular icon button), nor
 * keyboard behaviour (it has none), nor mobile behaviour (a Carousel) with this
 * one — see SH-312 Q10.
 *
 * The visual is ported from the legacy site's `.nav-tabs-gradient`: the two tabs
 * split the width, the inactive one is a muted grey panel with one rounded
 * corner top-outer and one bottom-inner, and the active one is white with both
 * top corners rounded, primary-colour text and a 2px primary underline inset
 * 18px from each side. The legacy CSS paints that through a `::before` at
 * `z-index: -1` only because Bootstrap's markup left it no other element; here
 * the radius sits on the button itself.
 */
export function HeroCardTabs({
  tabOneLabel,
  tabTwoLabel,
  tabOneAnchor,
  tabTwoAnchor,
  preselectedTab,
  renderTabOne,
  renderTabTwo,
  className,
  ...rest
}: HeroCardTabsProps) {
  const t = useTranslations('heroCards');
  const [active, setActive] = useState<PreselectedTab>(preselectedTab);
  const baseId = useId();
  const tabRefs = useRef<Record<PreselectedTab, HTMLButtonElement | null>>({
    one: null,
    two: null,
  });

  const tabId = (tab: PreselectedTab) => `${baseId}-tab-${tab}`;
  const panelId = (tab: PreselectedTab) => `${baseId}-panel-${tab}`;

  // Deep-linking: `/preise/#nachhilfe-online` opens tab 2 directly. The anchors
  // are CMS-configured per tab, like `widgets.location-finder.anchor`, so an
  // Editor picks fragments that read well in a shared link. Reading the hash in
  // an effect rather than during render keeps the server and first client
  // render identical — the server has no hash, so anything else hydrates wrong.
  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) {
        return;
      }
      if (tabOneAnchor && hash === tabOneAnchor) {
        setActive('one');
      } else if (tabTwoAnchor && hash === tabTwoAnchor) {
        setActive('two');
      }
    };
    applyHash();
    // Also honour an in-page link tapped after load, which changes the hash
    // without remounting.
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [tabOneAnchor, tabTwoAnchor]);

  // No GA4 event fires on a tab switch (SH-312 Q9) — deliberately, so nothing
  // here pushes tracking.
  const select = (tab: PreselectedTab, focus = false) => {
    setActive(tab);
    if (focus) {
      tabRefs.current[tab]?.focus();
    }
    // Keep the URL shareable, but with `replaceState` rather than a new hash:
    // pushing one would put every tab tap in the back-button history.
    const anchor = tab === 'one' ? tabOneAnchor : tabTwoAnchor;
    if (anchor) {
      window.history.replaceState(null, '', `#${anchor}`);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Home' ||
      event.key === 'End'
    ) {
      event.preventDefault();
      select(active === 'one' ? 'two' : 'one', true);
    }
  };

  const tabs: { key: PreselectedTab; label: string; anchor: string }[] = [
    { key: 'one', label: tabOneLabel, anchor: tabOneAnchor },
    { key: 'two', label: tabTwoLabel, anchor: tabTwoAnchor },
  ];

  return (
    <div {...rest} className={classNames('flex w-full flex-col', className)}>
      {/* Zero-height scroll targets, one per configured anchor — the same
          device the legacy page uses (`<span class="anchor" id="header-tabs">`),
          so `#fragment` lands on the card rather than jumping to a tab button
          mid-strip. Which fragment it is selects the tab, in the effect above. */}
      {tabs.map(({ key, anchor }) =>
        anchor ? (
          <span key={key} id={anchor} aria-hidden className="block h-0" />
        ) : null,
      )}
      {/* The filler sits in this wrapper rather than inside the tablist: a
          tablist's owned elements should be tabs, and nothing else. */}
      <div className="relative">
        {/* The two tabs meet at the midpoint, where each carves a 16px corner
            out of the strip: the active tab's top corner at the top, the
            inactive tab's bottom corner at the bottom. Without this the hero
            image shows through both notches. A 32px band centred on the
            junction fills them — grey above, white below — and stops short of
            the strip's outer top corners, which stay rounded over the image. */}
        <span
          aria-hidden
          className="absolute inset-y-0 left-1/2 z-0 w-8 -translate-x-1/2 bg-[linear-gradient(to_bottom,var(--color-surface-muted)_50%,#fff_50%)]"
        />
        <div
          role="tablist"
          aria-label={t('tabsLabel')}
          className="flex w-full"
          onKeyDown={onKeyDown}
        >
          {tabs.map(({ key, label }, i) => {
            const selected = active === key;
            const isLast = i === tabs.length - 1;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                id={tabId(key)}
                ref={(el) => {
                  tabRefs.current[key] = el;
                }}
                aria-selected={selected}
                aria-controls={panelId(key)}
                // Roving tabindex: only the active tab is in the tab order, and
                // the arrow keys move between them.
                tabIndex={selected ? 0 : -1}
                onClick={() => select(key)}
                className={classNames(
                  // `flex-1 basis-0` splits the strip evenly at every breakpoint —
                  // verified against the live site down to 320px, so there is no
                  // narrow-viewport special case (SH-312 O7).
                  'focus-visible:ring-primary relative z-[1] flex-1 basis-0 px-4 py-[18px] text-center text-sm/[1.4286] font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset md:px-5',
                  // The 2px underline, inset 18px each side, as on the legacy site.
                  'after:absolute after:inset-x-[18px] after:bottom-0 after:block after:h-0.5 after:transition-colors after:content-[""]',
                  selected
                    ? 'text-primary after:bg-primary rounded-t-2xl bg-white'
                    : classNames(
                        'text-secondary bg-surface-muted hover:text-primary after:bg-transparent',
                        // The inactive tab rounds its outer top corner and its
                        // inner bottom corner, so the two mirror each other.
                        isLast
                          ? 'rounded-tr-2xl rounded-bl-2xl'
                          : 'rounded-tl-2xl rounded-br-2xl',
                      ),
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {tabs.map(({ key }) => (
        <div
          key={key}
          role="tabpanel"
          id={panelId(key)}
          aria-labelledby={tabId(key)}
          // Focusable so a tab whose body is rich text only is still reachable
          // and scrollable with the keyboard (WAI-ARIA APG tabs pattern), with
          // its own ring since anything focusable needs a visible indicator.
          tabIndex={active === key ? 0 : undefined}
          className="focus-visible:ring-primary focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
          hidden={active !== key}
        >
          {active === key && (key === 'one' ? renderTabOne() : renderTabTwo())}
        </div>
      ))}
    </div>
  );
}
